import { useEffect, useState } from "react";
import { Card, Spinner } from "react-bootstrap";
import { useLanguage } from "../../../i18n/LanguageProvider";
import { DailySummary, SignalSource, useApi } from "../apiService";
import { relativeAgo, SourceChips, SourceModal, useSourceModal } from "./signalsShared";
import "./signals.css";

type Api = ReturnType<typeof useApi>;

// The daily "Nuläges-summering" recap card. Renders a bare Card so the caller
// controls placement. Fetches once on mount.
export const DailySummaryCard = ({ api }: { api: Api }) => {
  const { t, language, locale } = useLanguage();
  const [summary, setSummary] = useState<DailySummary | null>(null);
  const [sources, setSources] = useState<SignalSource[]>([]);
  const [loading, setLoading] = useState(true);
  const { activeGroup, setActiveGroup } = useSourceModal();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .fetchSignalSummary()
      .then((sum) => {
        if (cancelled) return;
        setSummary(sum.summary || null);
        setSources(sum.sources || []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setSummary(null);
        setSources([]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card className="shadow-card h-100">
      <Card.Body>
        <h5 className="signals-title">{t("signals.summaryTitle")}</h5>
        {loading ? (
          <div className="signals-loading">
            <Spinner animation="border" size="sm" /> {t("signals.loading")}
          </div>
        ) : summary ? (
          <>
            <p className="signals-summary-text mb-1">
              {language === "sv" ? summary.textSv : summary.textEn}
            </p>
            {summary.createdAt && (
              <div className="signals-summary-meta">
                {t("signals.updated", { ago: relativeAgo(summary.createdAt, locale) })}
              </div>
            )}
            <SourceChips sources={sources} onPick={setActiveGroup} />
          </>
        ) : (
          <div className="signals-empty">{t("signals.summaryEmpty")}</div>
        )}
      </Card.Body>
      <SourceModal group={activeGroup} onClose={() => setActiveGroup(null)} />
    </Card>
  );
};
